import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { GlobalSelectors, SiteActions, SiteSelectors } from '@app/store';
import { Store } from '@ngrx/store';
import { AppState } from '@app/store/app.state';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

@Component({
  selector: 'app-weekly-service-calendar',
  templateUrl: './weekly-service-calendar.component.html',
  styleUrls: ['./weekly-service-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WeeklyServiceCalendarComponent implements OnInit, OnDestroy {
  isAllServiceSchedulesLoading$: Observable<boolean> = this.store.select(
    SiteSelectors.isAllServiceSchedulesLoading
  );
  weekdayLookUpArray = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  todayDate = new Date();
  calendarDays = [];
  monthYearRangeString = '';
  carouselTransitionTimeStamp = new Date();
  subscriptions: Subscription = new Subscription();
  // weeklyMaxColumnHeight: string;
  serviceSchedules;
  displayWeek = 0;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.todayDate.setHours(0, 0, 0, 0);

    this.subscriptions.add(
      this.store
        .select(SiteSelectors.getAllActiveServiceSchedulesForCalendar)
        .subscribe(serviceSchedules => {
          this.calendarDays = [];
          this.todayDate.setHours(0, 0, 0, 0);
          this.serviceSchedules = serviceSchedules;

          if (this.calendarDays.length === 0) {
            this.buildWeek();
          }

          // update the month/year range
          this.calcMonthYearRange();
          this.cdr.detectChanges();
        })
    );

    this.subscriptions.add(
      this.store
        .select(GlobalSelectors.getSelectedGlobalFilters)
        .pipe(
          filter(
            selectedBillingAcc => selectedBillingAcc.billingAccounts.length > 0
          ),
          distinctUntilChanged((prev, curr) => {
            return JSON.stringify(prev) === JSON.stringify(curr);
          })
        )
        .subscribe(selectedBillingAcc => {
          this.store.dispatch(
            SiteActions.allAccountServiceSchedulesStartLoading()
          );
          this.store.dispatch(
            SiteActions.getAllAccountServiceSchedules({
              requestBody: selectedBillingAcc,
            })
          );
        })
    );
  }

  buildWeek(): void {
    const daysOfTheWeekSchedule: { date: Date; schedules: any[] }[] = [];

    // Calculate the starting Sunday of the week
    const weekStartDate = new Date(this.todayDate.getTime());

    weekStartDate.setDate(
      weekStartDate.getDate() +
        (0 - weekStartDate.getDay()) +
        this.displayWeek * 7
    );
    weekStartDate.setHours(0, 0, 0, 0);

    // Calculate the ending Saturday of the week
    const weekEndDate = new Date(weekStartDate.getTime());

    weekEndDate.setDate(weekEndDate.getDate() + 6);
    weekEndDate.setHours(23, 59, 59, 999);

    // Create a mapping of the date to the index and initialize the weekdays with date/array of schedules
    const datesToIndices: { [key: string]: number } = {};

    for (let i = 0; i < 7; i += 1) {
      const currentDate = new Date(weekStartDate.getTime());

      currentDate.setDate(currentDate.getDate() + i);
      daysOfTheWeekSchedule.push({ date: currentDate, schedules: [] });
      datesToIndices[currentDate.toLocaleDateString()] = i;
    }

    // Loop over each schedule and check if it fits in the week
    this.serviceSchedules.forEach(
      ({
        customerId,
        accountId,
        effectiveDate,
        expirationDate,
        isRecurringService,
        recurringFrequency,
        sunday,
        monday,
        tuesday,
        wednesday,
        thursday,
        friday,
        saturday,
        accountName,
        debris,
        accountServiceEquipment,
      }) => {
        const scheduleStartDate = Date.parse(effectiveDate);
        const scheduleEndDate = Date.parse(expirationDate);

        const scheduleCardDetails = {
          customerId,
          accountId,
          accountName,
          debris,
          accountServiceEquipment,
        };

        if (isRecurringService) {
          const weeksSinceStart = Math.floor(
            (weekEndDate.getTime() - scheduleStartDate) / 604800000
          ); // 604800000 ms in a week

          if (
            recurringFrequency === 0 ||
            recurringFrequency === 1 ||
            weeksSinceStart % recurringFrequency === 0
          ) {
            const intersectsCurrentWeek =
              (scheduleStartDate <= weekEndDate.getTime() &&
                scheduleEndDate >= weekStartDate.getTime()) ||
              (scheduleStartDate >= weekStartDate.getTime() &&
                scheduleStartDate <= weekEndDate.getTime()) ||
              (scheduleEndDate >= weekStartDate.getTime() &&
                scheduleEndDate <= weekEndDate.getTime());

            if (intersectsCurrentWeek) {
              // Check each day of the week
              if (sunday) {
                this.updateSchedules(
                  daysOfTheWeekSchedule[0],
                  scheduleCardDetails
                );
              }
              if (monday) {
                this.updateSchedules(
                  daysOfTheWeekSchedule[1],
                  scheduleCardDetails
                );
              }
              if (tuesday) {
                this.updateSchedules(
                  daysOfTheWeekSchedule[2],
                  scheduleCardDetails
                );
              }
              if (wednesday) {
                this.updateSchedules(
                  daysOfTheWeekSchedule[3],
                  scheduleCardDetails
                );
              }
              if (thursday) {
                this.updateSchedules(
                  daysOfTheWeekSchedule[4],
                  scheduleCardDetails
                );
              }
              if (friday) {
                this.updateSchedules(
                  daysOfTheWeekSchedule[5],
                  scheduleCardDetails
                );
              }
              if (saturday) {
                this.updateSchedules(
                  daysOfTheWeekSchedule[6],
                  scheduleCardDetails
                );
              }
            }
          }
        } else {
          const scheduleDateKey = new Date(effectiveDate).toLocaleDateString();

          if (scheduleDateKey in datesToIndices) {
            const index = datesToIndices[scheduleDateKey];
            const intersectsCurrentWeek =
              scheduleStartDate <= weekEndDate.getTime() &&
              scheduleEndDate >= weekStartDate.getTime();

            if (intersectsCurrentWeek) {
              daysOfTheWeekSchedule[index].schedules.push(scheduleCardDetails);
            }
          }
        }
      }
    );

    // Append this week of schedules to the array of weeks
    this.calendarDays.push(daysOfTheWeekSchedule);
  }

  updateSchedules(
    daysOfTheWeekSchedule: { date: Date; schedules: any[] },
    { customerId, accountId, accountName, debris, accountServiceEquipment }: any
  ): void {
    const newService = {
      customerId,
      accountId,
      accountName,
      debris,
      accountServiceEquipment,
      count: 1,
    };

    const foundSchedule = daysOfTheWeekSchedule.schedules.find(
      (schedule: any) => schedule.accountId === accountId
    );

    if (foundSchedule) {
      const foundService = foundSchedule.services.find(
        (service: any) =>
          service.debris === debris &&
          service.accountServiceEquipment === accountServiceEquipment
      );

      if (foundService) {
        foundService.count += 1;
      } else {
        foundSchedule.services.push(newService);
      }
    } else {
      daysOfTheWeekSchedule.schedules.push({
        customerId,
        accountId,
        accountName,
        services: [newService],
      });
    }
  }

  calcMonthYearRange(): void {
    const startDay = this.calendarDays[this.displayWeek][0].date;
    const endDay = this.calendarDays[this.displayWeek][6].date;
    const startMonth = startDay.toLocaleString('default', { month: 'short' });
    const endMonth = endDay.toLocaleString('default', { month: 'short' });
    const startYear = startDay.toLocaleString('default', { year: 'numeric' });
    const endYear = endDay.toLocaleString('default', { year: 'numeric' });

    this.monthYearRangeString = `${startMonth}`;

    if (startMonth !== endMonth) {
      this.monthYearRangeString += ` - ${endMonth}`;
    }

    this.monthYearRangeString += ` ${startYear}`;

    if (startYear !== endYear) {
      this.monthYearRangeString += ` - ${endYear}`;
    }
  }

  previousWeek(): void {
    // check previous week is not before starting point
    if (this.displayWeek > 0) {
      if (
        new Date().getTime() >
        this.carouselTransitionTimeStamp.getTime() + 600
      ) {
        this.carouselTransitionTimeStamp = new Date();
        this.displayWeek -= 1;

        this.calcMonthYearRange();
      }
    }
  }

  nextWeek(): void {
    // check next week is not after ending point
    if (this.displayWeek < 12) {
      if (
        new Date().getTime() >
        this.carouselTransitionTimeStamp.getTime() + 600
      ) {
        this.carouselTransitionTimeStamp = new Date();
        this.displayWeek += 1;

        // check if week hasn't already been calculated before (user could go back and then forward again)
        if (this.displayWeek === this.calendarDays.length) {
          this.buildWeek();
        }

        this.calcMonthYearRange();
      }
    }
  }

  // getColumnHeight(): void {
  //   const schedules = this.calendarDays[this.displayWeek].map(
  //     day => day.schedules.length
  //   );
  //   const tallest = Math.max(...schedules);

  //   // Calculate height based on tallest schedule block
  //   const height = Math.max(tallest * 78, 398); // Minimum height of 398px

  //   // Convert to rems
  //   // this.weeklyMaxColumnHeight = `${height / 16}rem`;
  // }

  onShowAllSites(): void {
    this.router.navigate(['organization/sites']);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
